/**
 * RCC Surface #14 — Dormant release processing.
 *
 * Manually release a dormant flag when the customer re-engages before the
 * escheatment deadline. Writes SessionEvent; Core Director update routed via
 * jXRL CustMod by RCM.
 */

import { useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button, Pill } from '@/components/primitives';
import { SurfaceHeader } from '../components/SurfaceHeader';

export function DormantRelease(): JSX.Element {
  const apiClient = useApiClient();
  const { user } = useAuth();
  const [cifId, setCifId] = useState<string>('');
  const [accountId, setAccountId] = useState<string>('');
  const [evidence, setEvidence] = useState<string>('');
  const [released, setReleased] = useState<boolean>(false);

  const release = (): void => {
    void apiClient.retail.events
      .write({
        eventType: 'dormant-release',
        eventCategory: 'compliance',
        severity: 'info',
        actor: user?.upn ?? 'unknown',
        payloadJson: JSON.stringify({ cifId, accountId, evidence }),
      })
      .catch(() => undefined);
    setReleased(true);
  };

  const canSubmit = cifId.trim() && accountId.trim() && evidence.trim();

  return (
    <div>
      <SurfaceHeader
        title="Dormant Release"
        scope="Release dormant flag when customer re-engages before escheatment deadline. Evidence (signature card, teller visit, deposit) is required."
        surfaceNumber={14}
        role="FSR · Supervisor approval"
      />

      <div style={{ display: 'grid', gap: 'var(--sp-5)', maxWidth: 640 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-5)' }}>
          <TextField label="CIF ID" value={cifId} onChange={setCifId} />
          <TextField label="Account #" value={accountId} onChange={setAccountId} />
        </div>
        <div>
          <label
            htmlFor="dr-evidence"
            style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 'var(--sp-3)' }}
          >
            Re-engagement evidence
          </label>
          <textarea
            id="dr-evidence"
            rows={4}
            value={evidence}
            onChange={(e) => setEvidence(e.target.value)}
            placeholder="E.g. Signed new signature card 2026-04-15; in-branch ID verification completed."
            style={{
              width: '100%',
              padding: 'var(--sp-4)',
              border: '1px solid var(--ink-300)',
              borderRadius: 'var(--r-md)',
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
            }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)' }}>
          <Button variant="primary" onClick={release} disabled={!canSubmit || released}>
            {released ? 'Released' : 'Release dormant flag'}
          </Button>
          {released && <Pill variant="ok">SessionEvent fired</Pill>}
        </div>
      </div>
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  const id = `dr-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div>
      <label
        htmlFor={id}
        style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 'var(--sp-3)' }}
      >
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: 'var(--sp-4)',
          border: '1px solid var(--ink-300)',
          borderRadius: 'var(--r-md)',
          fontSize: 13,
        }}
      />
    </div>
  );
}
